import React, { useState } from 'react';
import { History } from 'lucide-react';
import { ItemOverviewTransactionDto } from '../../../../../types/itemOverview';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { StatusBadge } from '../../../../common/StatusBadge';
import { isTrackedBatch, isTrackedSerial } from '../../../../../utils/itemType';
import { formatDateTime, formatQty } from '../format';
import { SectionCard } from './SectionCard';

interface ItemTransactionsSectionProps {
  transactions: ItemOverviewTransactionDto[];
  itemType: number | null | undefined;
}

// StatusType 27 — lagertransaksjonstype (StcTransaction.TransType)
const ST_STOCK_TRANS_TYPE = 27;
const COLLAPSED_COUNT = 10;

/** Siste lagerbevegelser (StcTransaction, maks 50 fra API), nyeste først. */
export const ItemTransactionsSection: React.FC<ItemTransactionsSectionProps> = ({ transactions, itemType }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const showTracking = isTrackedBatch(itemType) || isTrackedSerial(itemType);
  const visible = expanded ? transactions : transactions.slice(0, COLLAPSED_COUNT);

  const reference = (tx: ItemOverviewTransactionDto) => {
    if (tx.pListID) return `PL ${tx.pListID}`;
    if (tx.orderNo) return `#${tx.orderNo}`;
    if (tx.grShipmentNo) return `GR ${tx.grShipmentNo}`;
    return tx.reasonDescription || '';
  };

  return (
    <SectionCard
      icon={<History className="h-4 w-4" />}
      title={t('warehouse.modules.itemCard.transactions.title', {}, 'Siste bevegelser')}
      count={transactions.length}
    >
      {transactions.length === 0 ? (
        <p className="text-sm text-gray-500 py-2">{t('warehouse.modules.itemCard.transactions.empty', {}, 'Ingen lagerbevegelser registrert')}</p>
      ) : (
        <>
          <ul className="divide-y divide-gray-100">
            {visible.map(tx => (
              <li key={tx.id} className="flex items-center gap-3 py-2.5">
                <div className="flex-shrink-0 w-28 sm:w-36">
                  {tx.transType != null
                    ? <StatusBadge value={tx.transType} statusType={ST_STOCK_TRANS_TYPE} />
                    : <span className="text-xs text-gray-400">-</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-mono text-gray-900 break-words">{tx.binCode || '-'}</div>
                  <div className="text-xs text-gray-500 break-words">
                    {[formatDateTime(tx.transDate), tx.operatorID, reference(tx),
                      showTracking ? (tx.serialNo || tx.batchNo) : null].filter(v => v && v !== '-').join(' · ')}
                  </div>
                </div>
                <div className={`flex-shrink-0 text-sm font-semibold ${tx.quantity < 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {tx.quantity > 0 ? '+' : ''}{formatQty(tx.quantity)}
                </div>
              </li>
            ))}
          </ul>
          {transactions.length > COLLAPSED_COUNT && (
            <button
              type="button"
              onClick={() => setExpanded(v => !v)}
              className="mt-2 w-full min-h-[44px] rounded-lg text-sm font-medium text-blue-600 hover:bg-gray-50"
            >
              {expanded
                ? t('warehouse.modules.itemCard.transactions.showLess', {}, 'Vis færre')
                : t('warehouse.modules.itemCard.transactions.showAll', { '0': transactions.length }, `Vis alle ${transactions.length}`)}
            </button>
          )}
        </>
      )}
    </SectionCard>
  );
};
